import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Lock, Delete, ShieldCheck, KeyRound, Type, Coins,
  TrendingUp, Save, Camera, X, BadgeCheck, MapPin, Globe, Calendar, VenetianMask,
  ShoppingBag, Ticket, Wallet, Receipt, ArrowUpRight,
} from "lucide-react";
import { api } from "@/api/client";
import { useSession } from "@/context/SessionContext";
import { socialApi, resolveMedia } from "@/api/social";
import { vaultSecurityApi } from "@/api/vaultSecurity";
import { useFounder } from "@/components/dashboard/VaultStage7b";
import VaultPickButton from "@/components/mist/VaultPickButton";
import FinancialLockGate from "@/components/dashboard/FinancialLockGate";

const GENDERS = ["Male", "Female", "Non-Binary", "Prefer Not to Say"];

/* ============================ VAULT CONFIG ============================== */
/* Shown when NO credential is configured — define a custom PIN or passphrase. */
export function VaultConfig({ onDone, onBack }) {
  const { setUser } = useSession();
  const [mode, setMode] = useState("pin"); // pin | pass
  const [pin, setPin] = useState("");
  const [pass, setPass] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async (value) => {
    setBusy(true);
    try {
      await api.post("/auth/set-pin", { pin: value, mode });
      setUser((p) => ({ ...p, has_pin: true, vault_mode: mode }));
      toast.success("Vault credential set");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not set credential");
      setPin("");
    } finally { setBusy(false); }
  };

  const press = (d) => {
    if (busy || pin.length >= 6) return;
    const next = pin + d; setPin(next);
    if (next.length === 6) save(next);
  };

  return (
    <div className="absolute inset-0 z-30 flex flex-col bg-charcoal" style={{ boxShadow: "inset 0 0 0 3px #D4AF37" }} data-testid="vault-config">
      <header className="shrink-0 px-4 pt-4 pb-3 flex items-center gap-3">
        <button onClick={onBack} data-testid="vault-config-back" aria-label="Back" className="w-10 h-10 rounded-full bg-white/10 border-2 border-gold grid place-items-center shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"><ArrowLeft size={18} className="text-gold" /></button>
        <div className="leading-none">
          <p className="font-mono text-[9px] uppercase tracking-[0.28em] text-gold">First-time setup</p>
          <h1 className="font-serif text-2xl text-white">Secure the Vault</h1>
        </div>
      </header>
      <div className="px-6 pt-2">
        <div className="grid grid-cols-2 gap-2 bg-white/5 p-1 rounded-2xl border-2 border-gold/40">
          <button onClick={() => setMode("pin")} data-testid="vault-mode-pin" className={`flex items-center justify-center gap-1.5 font-mono text-[10px] font-black uppercase tracking-widest rounded-xl py-2.5 ${mode === "pin" ? "bg-gold text-black" : "text-white/60"}`}><KeyRound size={13} /> PIN</button>
          <button onClick={() => setMode("pass")} data-testid="vault-mode-pass" className={`flex items-center justify-center gap-1.5 font-mono text-[10px] font-black uppercase tracking-widest rounded-xl py-2.5 ${mode === "pass" ? "bg-gold text-black" : "text-white/60"}`}><Type size={13} /> Passphrase</button>
        </div>
      </div>

      {mode === "pin" ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-6 px-8">
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/50">Choose a 6-digit PIN</p>
          <div className="flex gap-2.5" data-testid="vault-config-dots">
            {Array.from({ length: 6 }).map((_, i) => <span key={i} className={`w-3.5 h-3.5 rounded-full border-2 ${i < pin.length ? "bg-gold border-gold" : "border-cream/40"}`} />)}
          </div>
          {busy && <Loader2 size={18} className="text-gold animate-spin" />}
          <div className="grid grid-cols-3 gap-3 w-full max-w-[260px]">
            {[1,2,3,4,5,6,7,8,9].map((n) => <button key={n} onClick={() => press(String(n))} data-testid={`vaultcfg-key-${n}`} className="h-14 rounded-2xl bg-white/10 border-2 border-gold/60 text-white font-mono text-xl font-bold shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">{n}</button>)}
            <span />
            <button onClick={() => press("0")} data-testid="vaultcfg-key-0" className="h-14 rounded-2xl bg-white/10 border-2 border-gold/60 text-white font-mono text-xl font-bold shadow-[3px_3px_0px_#000]">0</button>
            <button onClick={() => setPin((p) => p.slice(0, -1))} data-testid="vaultcfg-key-del" className="h-14 rounded-2xl bg-transparent border-2 border-cream/30 text-white/70 grid place-items-center"><Delete size={20} /></button>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center gap-5 px-8">
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/50">Set a bio passphrase (min 4 chars)</p>
          <input type="password" value={pass} onChange={(e) => setPass(e.target.value)} data-testid="vault-pass-input" className="w-full bg-white/10 border-2 border-gold rounded-xl px-4 py-3 font-mono text-sm text-white outline-none focus:ring-2 focus:ring-gold text-center tracking-widest" placeholder="••••••••" />
          <button onClick={() => pass.length >= 4 ? save(pass) : toast.error("Min 4 characters")} disabled={busy} data-testid="vault-pass-save" className="w-full bg-gold text-black font-black font-mono text-xs uppercase tracking-widest py-3 rounded-full border-2 border-charcoal shadow-[3px_3px_0px_#000] disabled:opacity-60 flex items-center justify-center gap-2">{busy ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />} Set Passphrase</button>
        </div>
      )}
    </div>
  );
}

/* ========================== VAULT CHALLENGE ============================= */
/* Shown when a credential IS configured — matches the chosen mode. */
export function VaultChallenge({ mode = "pin", onUnlock, onBack, onForgot, onLockout }) {
  const [pin, setPin] = useState("");
  const [pass, setPass] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {  // hide floating PiP so it can't block the PIN pad on mobile
    window.dispatchEvent(new CustomEvent("mist:pip-hide", { detail: { active: true } }));
    return () => window.dispatchEvent(new CustomEvent("mist:pip-hide", { detail: { active: false } }));
  }, []);

  const submit = async (value) => {
    setBusy(true); setError(false);
    try {
      await vaultSecurityApi.sessionPinUnlock(value, mode);
      onUnlock();  // PIN verified — the gateway opens the server session
    } catch (e) {
      const st = e?.response?.status;
      const d = e?.response?.data?.detail;
      if (st === 429 && d && d.seconds_remaining != null) {  // hard lockout
        setPin(""); onLockout?.(d.seconds_remaining); return;
      }
      setError(true); setPin("");
      const rem = d && d.attempts_remaining != null ? ` · ${d.attempts_remaining} left` : "";
      toast.error((mode === "pass" ? "Incorrect passphrase" : "Incorrect PIN") + rem);
    } finally { setBusy(false); }
  };

  const press = (d) => {
    if (busy || pin.length >= 4) return;
    const next = pin + d; setPin(next);
    if (next.length === 4) submit(next);
  };

  return (
    <div className="absolute inset-0 z-30 flex flex-col bg-charcoal" data-testid="vault-challenge">
      <header className="shrink-0 px-4 pt-4 pb-3 flex items-center gap-3">
        <button onClick={onBack} data-testid="vault-challenge-back" aria-label="Back" className="w-10 h-10 rounded-full bg-white/10 border-2 border-gold grid place-items-center shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"><ArrowLeft size={18} className="text-gold" /></button>
        <div className="leading-none">
          <p className="font-mono text-[9px] uppercase tracking-[0.28em] text-gold">{mode === "pass" ? "Passphrase Required" : "Master PIN Required"}</p>
          <h1 className="font-serif text-2xl text-white">Secure Vault</h1>
        </div>
      </header>

      {mode === "pass" ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-5 px-8">
          <Lock size={30} className={`text-gold ${error ? "animate-shake" : ""}`} />
          <input type="password" value={pass} onChange={(e) => setPass(e.target.value)} onKeyDown={(e) => e.key === "Enter" && pass && submit(pass)} data-testid="vault-challenge-pass" className={`w-full bg-white/10 border-2 rounded-xl px-4 py-3 font-mono text-sm text-white outline-none text-center tracking-widest ${error ? "border-red-500" : "border-gold"}`} placeholder="Enter passphrase" autoFocus />
          <button onClick={() => pass && submit(pass)} disabled={busy} data-testid="vault-challenge-submit" className="w-full bg-gold text-black font-black font-mono text-xs uppercase tracking-widest py-3 rounded-full border-2 border-charcoal shadow-[3px_3px_0px_#000] disabled:opacity-60 flex items-center justify-center gap-2">{busy ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />} Unlock</button>
          {onForgot && (
            <button onClick={onForgot} data-testid="vault-forgot-btn" className="font-mono text-[10px] uppercase tracking-widest text-gold/80 underline underline-offset-4">Forgot Credentials?</button>
          )}
        </div>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center gap-6 px-8">
          <div className="flex gap-2.5" data-testid="vault-challenge-dots">
            {Array.from({ length: 4 }).map((_, i) => <span key={i} className={`w-3.5 h-3.5 rounded-full border-2 ${error ? "border-red-500" : i < pin.length ? "bg-gold border-gold" : "border-cream/40"}`} />)}
          </div>
          {busy && <Loader2 size={18} className="text-gold animate-spin" />}
          <div className="grid grid-cols-3 gap-3 w-full max-w-[260px]">
            {[1,2,3,4,5,6,7,8,9].map((n) => <button key={n} onClick={() => press(String(n))} data-testid={`vault-key-${n}`} className="h-14 rounded-2xl bg-white/10 border-2 border-gold/60 text-white font-mono text-xl font-bold shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">{n}</button>)}
            <span />
            <button onClick={() => press("0")} data-testid="vault-key-0" className="h-14 rounded-2xl bg-white/10 border-2 border-gold/60 text-white font-mono text-xl font-bold shadow-[3px_3px_0px_#000]">0</button>
            <button onClick={() => setPin((p) => p.slice(0, -1))} data-testid="vault-key-del" className="h-14 rounded-2xl bg-transparent border-2 border-cream/30 text-white/70 grid place-items-center"><Delete size={20} /></button>
          </div>
          {onForgot && (
            <button onClick={onForgot} data-testid="vault-forgot-btn" className="font-mono text-[10px] uppercase tracking-widest text-gold/80 underline underline-offset-4">Forgot Credentials?</button>
          )}
        </div>
      )}
    </div>
  );
}

/* ========================= WEALTH COIN MONITOR ========================== */
/* M-Token redemption sink — spend gamification tokens for real perks. */
function MTokenSink({ onCredited }) {
  const [data, setData] = useState(null);
  const [gam, setGam] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => {
    vaultSecurityApi.gamifyRedemptions().then(setData).catch(() => {});
    vaultSecurityApi.gamification().then(setGam).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const redeem = async (key) => {
    setBusy(key);
    try {
      const r = await vaultSecurityApi.gamifyRedeem(key);
      toast.success(r.milink_credited ? `+${r.milink_credited} MiLink credited!` : `Unlocked: ${r.label}`);
      if (r.milink_credited) onCredited?.();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Redemption failed"); }
    finally { setBusy(""); }
  };

  const ICONS = { ltd_discount: Ticket, milink_topup: Wallet };
  return (
    <div className="border-4 border-charcoal rounded-2xl bg-white p-3 shadow-hard" data-testid="wealth-mtoken-sink">
      <div className="flex items-center justify-between mb-2">
        <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/50 flex items-center gap-1"><ShoppingBag size={12} className="text-charcoal" /> M-Token Redemption Sink</p>
        <span className="font-mono text-[10px] font-black text-charcoal" data-testid="wealth-mtoken-balance">{gam?.mTokens ?? 0} tokens</span>
      </div>
      {gam?.ltdDiscountUsd > 0 && (
        <div className="mb-2 font-mono text-[9px] text-emerald-700 border-2 border-emerald-500/50 rounded-lg px-2 py-1" data-testid="wealth-ltd-entitlement">Active: ${gam.ltdDiscountUsd} OFF Lifetime Deal</div>
      )}
      <div className="space-y-2">
        {(data?.options || []).map((o) => {
          const Icon = ICONS[o.key] || ShoppingBag;
          return (
            <div key={o.key} className="flex items-center gap-2 border-2 border-charcoal/15 rounded-xl bg-cream p-2.5" data-testid={`wealth-sink-${o.key}`}>
              <Icon size={16} className="text-charcoal shrink-0" />
              <div className="flex-1 leading-tight min-w-0">
                <p className="font-mono text-[10px] font-black text-charcoal truncate">{o.label}</p>
                <p className="font-mono text-[8px] text-charcoal/50">{o.cost} M-Tokens</p>
              </div>
              <button onClick={() => redeem(o.key)} disabled={!o.affordable || busy === o.key} data-testid={`wealth-sink-redeem-${o.key}`}
                className={`shrink-0 flex items-center justify-center gap-1 border-2 font-mono text-[9px] font-black uppercase tracking-widest px-3 py-2 rounded-full ${o.affordable ? "bg-charcoal border-charcoal text-gold shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none" : "bg-cream border-charcoal/20 text-charcoal/40"}`}>
                {busy === o.key ? <Loader2 size={11} className="animate-spin" /> : <Coins size={11} />} {o.affordable ? "Redeem" : "Locked"}
              </button>
            </div>
          );
        })}
        {(!data?.options || data.options.length === 0) && (
          <p className="font-mono text-[10px] text-charcoal/45 text-center py-3">Earn M-Tokens in the vault streak & wheel to unlock perks.</p>
        )}
      </div>
    </div>
  );
}

export function WealthMonitor({ onBack, onOpenTax }) {
  const { wallet, refreshWallet, refreshUser } = useSession();
  const founder = useFounder();
  const [metrics, setMetrics] = useState(null);
  const [balance, setBalance] = useState(wallet?.balance ?? null);

  const TaxCard = onOpenTax ? (
    <button onClick={onOpenTax} data-testid="wealth-tax-btn"
      className="w-full flex items-center justify-between border-4 border-charcoal rounded-2xl bg-charcoal px-4 py-3.5 shadow-[4px_4px_0px_#1A1A1A] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none transition-transform">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl border-2 border-gold grid place-items-center bg-white/5"><Receipt size={20} strokeWidth={2.2} className="text-gold" /></span>
        <div className="text-left leading-tight">
          <p className="font-serif text-lg text-white">Tax &amp; Books</p>
          <p className="font-mono text-[8px] uppercase tracking-[0.18em] text-gold/80">Income · Expenses · Liability · PDF</p>
        </div>
      </div>
      <ArrowUpRight size={18} className="text-gold" />
    </button>
  ) : null;

  useEffect(() => {
    if (!founder) return undefined;
    const load = async () => {
      try {
        const w = await refreshWallet?.();
        if (w?.balance != null) setBalance(w.balance);
        const { data } = await api.post("/wealth-money/invoke", { action: "stream_financial_metrics", timeline_days: 30 });
        setMetrics(data);
      } catch { /* offline */ }
    };
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [founder]);

  if (!founder) {
    return (
      <div className="absolute inset-0 z-30 flex flex-col" style={{ backgroundColor: "#9EFFB8" }} data-testid="wealth-monitor">
        <header className="shrink-0 px-4 pt-4 pb-3 flex items-center gap-3">
          <button onClick={onBack} data-testid="wealth-back-btn" aria-label="Back" className="w-10 h-10 rounded-full bg-white border-2 border-charcoal grid place-items-center shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"><ArrowLeft size={18} strokeWidth={2.4} /></button>
          <div className="leading-none">
            <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-charcoal/60">Wealth & Money</p>
            <h1 className="font-serif text-2xl text-charcoal">Credit Monitor</h1>
          </div>
        </header>
        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pb-6 space-y-4">
          {TaxCard}
          <FinancialLockGate testid="wealth-lock" />
        </div>
      </div>
    );
  }

  return (
    <div className="absolute inset-0 z-30 flex flex-col" style={{ backgroundColor: "#9EFFB8" }} data-testid="wealth-monitor">
      <header className="shrink-0 px-4 pt-4 pb-3 flex items-center gap-3">
        <button onClick={onBack} data-testid="wealth-back-btn" aria-label="Back" className="w-10 h-10 rounded-full bg-white border-2 border-charcoal grid place-items-center shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"><ArrowLeft size={18} strokeWidth={2.4} /></button>
        <div className="leading-none">
          <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-charcoal/60">Wealth & Money</p>
          <h1 className="font-serif text-2xl text-charcoal">Credit Monitor</h1>
        </div>
      </header>
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pb-6 space-y-4">
        {TaxCard}
        <div className="border-4 border-charcoal rounded-3xl bg-charcoal p-5 shadow-[5px_5px_0px_#1A1A1A] text-center" data-testid="coin-counter">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold mb-2">MiLink Balance</p>
          <div className="flex items-center justify-center gap-2">
            <Coins size={30} className="text-gold" />
            <span className="font-serif text-5xl text-white tabular-nums" data-testid="coin-counter-value">{balance == null ? "—" : Math.round(balance).toLocaleString()}</span>
          </div>
          <p className="font-mono text-[9px] uppercase tracking-widest text-white/50 mt-2">system state monitor · live</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="border-4 border-charcoal rounded-2xl bg-white p-3 shadow-hard">
            <p className="font-mono text-[8px] uppercase tracking-widest text-charcoal/50">Net after tax</p>
            <p className="font-serif text-2xl text-charcoal tabular-nums">{metrics?.net_after_tax == null ? "—" : Math.round(metrics.net_after_tax).toLocaleString()}</p>
          </div>
          <div className="border-4 border-charcoal rounded-2xl bg-white p-3 shadow-hard">
            <p className="font-mono text-[8px] uppercase tracking-widest text-charcoal/50">Txns (30d)</p>
            <p className="font-serif text-2xl text-charcoal tabular-nums">{metrics?.txn_count ?? "—"}</p>
          </div>
        </div>
        <div className="border-4 border-charcoal rounded-2xl bg-white p-3 shadow-hard">
          <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/50 mb-2 flex items-center gap-1"><TrendingUp size={12} className="text-gold" /> Daily net (30d)</p>
          {metrics?.series?.length ? (
            <div className="flex items-end gap-1 h-24">
              {metrics.series.slice(-14).map((s, i) => {
                const max = Math.max(...metrics.series.map((x) => Math.abs(x.net)), 1);
                const h = Math.max(6, (Math.abs(s.net) / max) * 88);
                return <div key={i} className="flex-1 rounded-t bg-charcoal" style={{ height: `${h}%` }} title={`${s.date}: ${s.net}`} />;
              })}
            </div>
          ) : <p className="font-mono text-[10px] text-charcoal/40 py-4 text-center">No transactions in range.</p>}
        </div>
        <MTokenSink onCredited={async () => { const w = await refreshWallet?.(); if (w?.balance != null) setBalance(w.balance); refreshUser?.(); }} />
      </div>
    </div>
  );
}

/* ======================= 7-POINT PROFILE EDITOR ========================= */
export function ProfileEditor7({ onBack }) {
  const { user, setUser, token } = useSession();
  const identityVerified = !!(user?.identity_verified || user?.verified);
  const [form, setForm] = useState({
    name: user?.name || "", location: user?.location || "", language: user?.language || "English",
    bio: user?.bio || "", birth_date: user?.birth_date || "", gender: user?.gender || "",
  });
  const [saving, setSaving] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const avatarRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [camOpen, setCamOpen] = useState(false);
  const [capturing, setCapturing] = useState(false);

  const avatarSrc = avatarPreview || resolveMedia(user?.avatar_url, token);
  const avatarMissing = !avatarSrc;                 // guardrail trips on EMPTY avatar
  const avatarOk = !!avatarSrc && identityVerified; // fully-verified badge

  const commitAvatar = async (file, previewUrl) => {
    setAvatarPreview(previewUrl); setAvatarBusy(true);
    try {
      const res = await socialApi.upload(file);
      const { user: updated } = await socialApi.updateProfile({ avatar_url: `/api/social/media/${res.id}` });
      setUser((p) => ({ ...p, ...updated }));
      toast.success("Verified photo uploaded");
    } catch (err) { toast.error("Upload failed"); setAvatarPreview(null); } finally { setAvatarBusy(false); }
  };

  const onAvatar = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    await commitAvatar(file, URL.createObjectURL(file));
  };

  const openCamera = async () => {
    setCamOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play().catch(() => {}); }
    } catch (err) { toast.error("Camera unavailable"); setCamOpen(false); }
  };

  const closeCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCamOpen(false);
  };

  const capture = async () => {
    const v = videoRef.current;
    if (!v) return;
    setCapturing(true);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = v.videoWidth || 480; canvas.height = v.videoHeight || 480;
      canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.9));
      const file = new File([blob], `verify_${Date.now()}.jpg`, { type: "image/jpeg" });
      closeCamera();
      await commitAvatar(file, canvas.toDataURL("image/jpeg"));
    } finally { setCapturing(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { user: updated } = await socialApi.updateProfile(form);
      setUser((p) => ({ ...p, ...updated }));
      toast.success("Profile saved");
      onBack();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  const fields = [
    { key: "name", label: "1 · Display Name", icon: null },
    { key: "location", label: "2 · Location", icon: MapPin },
    { key: "language", label: "3 · Language", icon: Globe },
  ];

  return (
    <div className="absolute inset-0 z-30 flex flex-col" style={{ backgroundColor: "#FFD700" }} data-testid="profile-editor-7">
      <header className="shrink-0 px-4 pt-4 pb-3 flex items-center gap-3">
        <button onClick={onBack} data-testid="editor7-back-btn" aria-label="Back" className="w-10 h-10 rounded-full bg-white border-2 border-charcoal grid place-items-center shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"><ArrowLeft size={18} strokeWidth={2.4} /></button>
        <div className="leading-none">
          <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-charcoal/60">Career · Identity</p>
          <h1 className="font-serif text-2xl text-charcoal">7-Point Editor</h1>
        </div>
      </header>
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pb-4 space-y-3">
        {/* UNVERIFIED image guardrail — blinking banner freezes save until avatar upload */}
        {avatarMissing && (
          <div className="animate-blink border-4 border-charcoal rounded-2xl bg-red-600 px-3 py-2.5 flex items-center gap-2 shadow-[3px_3px_0px_#1A1A1A]" data-testid="editor7-guardrail-banner">
            <X size={16} strokeWidth={3} className="text-white shrink-0" />
            <p className="font-mono text-[10px] font-black uppercase tracking-widest text-white leading-tight">Unverified — upload a profile image via camera to unlock saving</p>
          </div>
        )}
        {/* 7 · Avatar with UNVERIFIED image guardrail */}
        <div className="border-4 border-charcoal rounded-2xl bg-white p-3 shadow-hard flex items-center gap-3" data-testid="editor7-avatar-block">
          <input ref={avatarRef} type="file" accept="image/*" hidden onChange={onAvatar} data-testid="editor7-avatar-input" />
          <button onClick={() => avatarRef.current?.click()} className="relative w-16 h-16 rounded-full border-4 border-charcoal overflow-hidden bg-cream grid place-items-center shrink-0">
            {avatarSrc ? <img src={avatarSrc} alt="avatar" className="w-full h-full object-cover" /> : <Camera size={20} className="text-charcoal/50" />}
            <span className="absolute inset-0 bg-black/30 opacity-0 active:opacity-100 grid place-items-center">{avatarBusy ? <Loader2 size={16} className="text-white animate-spin" /> : <Camera size={16} className="text-white" />}</span>
          </button>
          <div className="min-w-0">
            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/50">7 · Profile Image</p>
            {avatarOk ? (
              <span data-testid="editor7-verified" className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-500 text-white border-2 border-charcoal px-2.5 py-0.5 font-mono text-[9px] font-black uppercase"><BadgeCheck size={12} /> Verified</span>
            ) : (
              <span data-testid="editor7-unverified" className="mt-1 inline-flex items-center gap-1 rounded-full bg-red-600 text-white border-2 border-charcoal px-2.5 py-0.5 font-mono text-[9px] font-black uppercase"><X size={11} strokeWidth={3} /> Unverified</span>
            )}
            {!avatarOk && <p className="mt-1 font-mono text-[8px] text-charcoal/50 leading-snug">Upload a clear avatar & verify identity to clear the guardrail.</p>}
            <button onClick={openCamera} data-testid="editor7-camera-btn" className="mt-2 inline-flex items-center gap-1.5 bg-charcoal text-gold font-mono text-[9px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full border-2 border-gold shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none">
              <Camera size={12} /> Verify with Camera
            </button>
            <VaultPickButton onFile={(f) => onAvatar({ target: { files: [f], value: "" } })} bucket="images" testid="editor7-avatar-vault" label="From Vault"
              className="mt-2 ml-2 inline-flex items-center gap-1.5 bg-black text-[#00F5D4] font-mono text-[9px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full border-2 border-[#00F5D4] active:translate-y-[1px]" />
          </div>
        </div>
        {/* Live device camera capture modal */}
        {camOpen && (
          <div className="absolute inset-0 z-50 bg-black flex flex-col" data-testid="editor7-camera-modal">
            <div className="flex-1 relative grid place-items-center overflow-hidden">
              <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" data-testid="editor7-camera-video" />
              <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: "inset 0 0 0 3px rgba(212,175,55,0.8)" }} />
            </div>
            <div className="shrink-0 flex items-center justify-between gap-3 p-4 bg-charcoal">
              <button onClick={closeCamera} data-testid="editor7-camera-cancel" className="font-mono text-[10px] uppercase tracking-widest text-white/60 px-3 py-2">Cancel</button>
              <button onClick={capture} disabled={capturing} data-testid="editor7-camera-capture" className="flex items-center gap-2 bg-gold text-black font-black font-mono text-xs uppercase tracking-widest px-6 py-3 rounded-full border-2 border-cream shadow-[3px_3px_0px_#000] disabled:opacity-60">
                {capturing ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />} Capture
              </button>
              <span className="w-14" />
            </div>
          </div>
        )}

        {fields.map((f) => (
          <label key={f.key} className="block">
            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/60 flex items-center gap-1">{f.icon && <f.icon size={11} className="text-charcoal" />}{f.label}</span>
            <input value={form[f.key]} onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))} data-testid={`editor7-${f.key}`} className="mt-1 w-full bg-white border-2 border-charcoal rounded-xl px-3 py-2 font-mono text-xs outline-none focus:ring-2 focus:ring-charcoal shadow-[2px_2px_0px_#1A1A1A]" />
          </label>
        ))}
        <label className="block">
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/60">4 · Bio</span>
          <textarea value={form.bio} rows={3} onChange={(e) => setForm((s) => ({ ...s, bio: e.target.value }))} data-testid="editor7-bio" className="mt-1 w-full bg-white border-2 border-charcoal rounded-xl px-3 py-2 font-mono text-xs outline-none focus:ring-2 focus:ring-charcoal resize-none shadow-[2px_2px_0px_#1A1A1A]" />
        </label>
        <label className="block">
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/60 flex items-center gap-1"><Calendar size={11} className="text-charcoal" />5 · Date of Birth</span>
          <input type="date" value={form.birth_date || ""} onChange={(e) => setForm((s) => ({ ...s, birth_date: e.target.value }))} data-testid="editor7-birth_date" className="mt-1 w-full bg-white border-2 border-charcoal rounded-xl px-3 py-2 font-mono text-xs outline-none focus:ring-2 focus:ring-charcoal shadow-[2px_2px_0px_#1A1A1A]" />
        </label>
        <label className="block">
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/60 flex items-center gap-1"><VenetianMask size={11} className="text-charcoal" />6 · Gender</span>
          <select value={form.gender || ""} onChange={(e) => setForm((s) => ({ ...s, gender: e.target.value }))} data-testid="editor7-gender" className="mt-1 w-full bg-white border-2 border-charcoal rounded-xl px-3 py-2 font-mono text-xs outline-none focus:ring-2 focus:ring-charcoal shadow-[2px_2px_0px_#1A1A1A]">
            <option value="">Select…</option>
            {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </label>
      </div>
      <div className="shrink-0 border-t-4 border-charcoal bg-white px-4 py-3">
        <button onClick={save} disabled={saving || avatarMissing} data-testid="editor7-save-btn" className="w-full bg-charcoal text-gold font-black font-mono text-xs uppercase tracking-widest py-3 rounded-full border-2 border-gold shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-50 flex items-center justify-center gap-2">{saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {avatarMissing ? "Locked — upload avatar" : "Save Profile"}</button>
      </div>
    </div>
  );
}

/* ===================== BUSINESS TOOLS (conversion) ====================== */
export function BusinessTools({ onBack }) {
  const [deal, setDeal] = useState(null);
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { api.get("/promo/deal").then((r) => setDeal(r.data)).catch(() => {}); }, []);

  const submit = async () => {
    const email = form.email.trim().toLowerCase();
    if (!/^\S+@\S+\.\S+$/.test(email)) { toast.error("Enter a valid email"); return; }
    setBusy(true);
    try {
      const r = await api.post("/promo/lead", { email, name: form.name.trim(), source: "reels_ltd" });
      setDeal((d) => (d ? { ...d, seats_remaining: r.data.seats_remaining } : d));
      setDone(true);
      toast.success(r.data.already_registered ? "You're already on the list!" : "Seat reserved 🎉");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit"); } finally { setBusy(false); }
  };

  return (
    <div className="absolute inset-0 z-30 flex flex-col" style={{ backgroundColor: "#A3E2FF" }} data-testid="business-tools">
      <header className="shrink-0 px-4 pt-4 pb-3 flex items-center gap-3">
        <button onClick={onBack} data-testid="business-back-btn" aria-label="Back" className="w-10 h-10 rounded-full bg-white border-2 border-charcoal grid place-items-center shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"><ArrowLeft size={18} strokeWidth={2.4} /></button>
        <div className="leading-none">
          <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-charcoal/60">Business Tools</p>
          <h1 className="font-serif text-2xl text-charcoal">Growth Deck</h1>
        </div>
      </header>
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pb-6 space-y-4">
        {/* Conversion hero */}
        <div className="border-4 border-charcoal rounded-3xl bg-charcoal p-5 shadow-[5px_5px_0px_#1A1A1A]">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Creator Growth Engine</p>
          <h2 className="font-serif text-3xl text-white leading-none mt-1">Turn reels into revenue.</h2>
          <p className="font-mono text-[10px] text-white/60 mt-2 leading-relaxed">Ship content, monetize rooms, and grow your audience — all from one thumb-sized cockpit.</p>
        </div>

        {/* Reels Lifetime Deal layer */}
        <div className="border-4 border-charcoal rounded-3xl bg-white p-4 shadow-hard" data-testid="ltd-card">
          <div className="flex items-center justify-between">
            <span className="font-mono text-[9px] font-black uppercase tracking-widest bg-gold text-black border-2 border-charcoal rounded-full px-2.5 py-0.5">Lifetime Deal</span>
            {deal && <span className="font-mono text-[9px] font-black uppercase tracking-widest text-red-600" data-testid="ltd-seats">{deal.seats_remaining} seats left</span>}
          </div>
          <h3 className="font-serif text-2xl text-charcoal leading-none mt-2">{deal?.title || "Reels Lifetime Deal"}</h3>
          <p className="font-mono text-[10px] text-charcoal/60 mt-1">{deal?.tagline}</p>
          <div className="flex items-end gap-2 mt-3">
            <span className="font-serif text-4xl text-charcoal">${deal?.price_usd ?? 149}</span>
            <span className="font-mono text-xs text-charcoal/40 line-through mb-1.5">${deal?.was_usd ?? 588}</span>
          </div>
          <ul className="mt-2 space-y-1">
            {(deal?.perks || []).map((p) => (
              <li key={p} className="flex items-start gap-1.5 font-mono text-[10px] text-charcoal/80"><BadgeCheck size={12} className="text-emerald-500 mt-0.5 shrink-0" />{p}</li>
            ))}
          </ul>
        </div>

        {/* Lead capture — email normalized via .trim().toLowerCase() */}
        <div className="border-4 border-charcoal rounded-3xl bg-white p-4 shadow-hard" data-testid="lead-form">
          {done ? (
            <div className="text-center py-3">
              <BadgeCheck size={28} className="text-emerald-500 mx-auto" />
              <p className="font-serif text-xl text-charcoal mt-1">You're on the list!</p>
              <p className="font-mono text-[10px] text-charcoal/50 mt-1">We'll email your lifetime access details.</p>
            </div>
          ) : (
            <>
              <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/50 mb-2">Claim your seat</p>
              <input value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} data-testid="lead-name" placeholder="Name" className="w-full mb-2 bg-cream border-2 border-charcoal rounded-xl px-3 py-2.5 font-mono text-xs outline-none focus:ring-2 focus:ring-gold shadow-[2px_2px_0px_#1A1A1A]" />
              <input value={form.email} onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))} data-testid="lead-email" placeholder="you@email.com" className="w-full mb-3 bg-cream border-2 border-charcoal rounded-xl px-3 py-2.5 font-mono text-xs outline-none focus:ring-2 focus:ring-gold shadow-[2px_2px_0px_#1A1A1A]" />
              <button onClick={submit} disabled={busy} data-testid="lead-submit" className="w-full bg-gold text-black font-black font-mono text-xs uppercase tracking-widest py-3 rounded-full border-2 border-charcoal shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60 flex items-center justify-center gap-2">{busy ? <Loader2 size={15} className="animate-spin" /> : <TrendingUp size={15} />} Reserve My Seat</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
